import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiToast } from '@hooks/useApiToast';
import { bffApiClient } from '@api/bff/client';
import { WorkOrdersApi } from '@api/bff/openapi';
import type { WorkOrderViewModel } from '@api/bff/openapi';
import { useWorkOrdersStore } from '@stores/workOrdersStore';
import { useUserStore } from '@stores/userStore';
import { workOrderKeys } from '../constants';
import { db } from '@db/database';
import { enqueue, processAll } from '@services/offlineSyncService';

type CompleteWorkOrderParams = {
  jobNumber: string;
  subStatus?: string;
};

export const useCompleteWorkOrder = (onSuccessCallback?: () => void) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<WorkOrderViewModel, Error, CompleteWorkOrderParams>({
    mutationFn: async ({ jobNumber, subStatus }: CompleteWorkOrderParams) => {
      if (!navigator.onLine) {
        await db.transaction(
          'rw',
          [db.cachedWorkOrders, db.syncQueue],
          async () => {
            const cached = await db.cachedWorkOrders
              .where('jobNumber')
              .equals(jobNumber)
              .first();

            if (cached) {
              await db.cachedWorkOrders.update(jobNumber, {
                status: 'Complete' as never
              });
            }

            // Find pending detail items for dependency chain
            const userId = useUserStore.getState().userName ?? '';
            const dependentItems = await db.syncQueue
              .where('_userId')
              .equals(userId)
              .and(
                i =>
                  (i.entityType === 'wo_detail_complete' ||
                    i.entityType === 'wo_detail_skip' ||
                    i.entityType === 'wo_document_create') &&
                  (i.payload as Record<string, unknown>).jobNumber ===
                    jobNumber &&
                  i.status !== 'synced'
              )
              .toArray();

            const dependsOn = dependentItems.map(i => i.id!).filter(Boolean);

            await enqueue({
              entityType: 'wo_complete',
              entityKey: jobNumber,
              payload: {
                jobNumber,
                subStatus,
                originalState: cached?.status
              },
              priority: 60,
              dependsOn
            });
          }
        );

        return {
          jobNumber,
          status: 'Complete'
        } as unknown as WorkOrderViewModel;
      }

      const api = new WorkOrdersApi(undefined, undefined, bffApiClient);
      const response = await api.apiWorkOrdersJobNumberCompletePost({
        jobNumber,
        completeWorkOrderCommand: { subStatus: subStatus || undefined }
      });
      return response.data;
    },
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: workOrderKeys.byJobNumber(variables.jobNumber)
      });
      queryClient.invalidateQueries({
        queryKey: workOrderKeys.odata()
      });
      useWorkOrdersStore.getState().clearWorkOrders();

      showSuccess('submitAcknowledgment.success', 'customerReview');
      onSuccessCallback?.();

      if (!navigator.onLine) {
        processAll();
      }

      if (navigator.onLine) {
        try {
          const metadata = await db.downloadMetadata.get(
            String(variables.jobNumber)
          );
          if (metadata?.status === 'cached') {
            await db.cachedWorkOrders.update(String(variables.jobNumber), {
              status: 'Complete' as never
            });
          }
        } catch {
          // Best effort
        }
      }
    },
    onError: (error, _variables) => {
      showError('submitAcknowledgment.error', error, 'customerReview');
    }
  });
};
